import { useState } from "react";
import { useParams } from "react-router-dom";

import Grid from "@Components/Grid";
import Circle from "@Components/Circle";
import Button from "@Components/Button";
import BackButton from "@Components/BackButton";
import { useGetTicket } from "@Hooks/useTickets";
import { useUpdateTicket } from "@Hooks/useTickets";
import { LenderTicket, Status } from "@Types/tickets";
import { capitalize, formatDate } from "@Utils/functions";
import LoadingFullScreen from "@Components/Loading/LoadingFullScreen";
import { solvedToResolved } from "@Pages/LatestTickets/LatestTickets";
import { formatCustomersNames } from "@Pages/Applications/Applications";

import LenderNotes from "./LenderNotes";
import styles from "./Tickets.module.scss";
import DmvCheckDialog from "./DmvCheckDialog";
import { priorityToColor, statusStyle, ticketStatusToColor } from "./Tickets";

const renderDocuments = (
  ticket: LenderTicket,
  title: string,
  docType: "titleRegistrationInfo" | "oldTitleRegistrationInfo" | "receiptInfo"
) =>
  ticket.data.titleIssue?.data.info?.[docType] &&
  ticket.data.titleIssue?.data.info?.[docType]?.documents?.length > 0 ? (
    <div>
      <div className={styles.value}>{title}</div>
      <div className={styles.documents}>
        {ticket.data.titleIssue?.data.info?.[docType]?.documents.map(
          (doc, index) => (
            <a
              key={`${index}-${title}`}
              className={styles.documentLink}
              href={doc}
              target="_blank"
              rel="noopener noreferrer"
            >
              {`Document ${index + 1}`}
            </a>
          )
        )}
      </div>
    </div>
  ) : (
    <div className={styles.documents}>
      <div className={styles.value}>{`No ${title} uploaded.`}</div>
    </div>
  );

export default function Ticket() {
  const [open, setOpen] = useState(false);
  const { id } = useParams<{ id: string }>();
  const { ticket } = useGetTicket(id as string);
  const { updateTicket } = useUpdateTicket();
  const handleOpenDialog = () => setOpen(true);

  const handleCloseTicket = () => {
    if (ticket)
      updateTicket({
        ...ticket,
        data: {
          ...ticket.data,
          info: { ...ticket.data.info, status: Status.Sovled },
        },
      });
  };

  if (!id) return <>Error</>;
  if (!ticket) return <LoadingFullScreen />;
  return (
    <Grid className={styles.container} xs={12} container spacing={1}>
      <Grid xs={12}>
        <BackButton />
      </Grid>
      <Grid xs={11}>
        <h2 className={styles.title}>Ticket overview</h2>
      </Grid>
      <Grid xs={1}>
        {ticket?.data.info.status !== Status.Sovled && (
          <Button
            variant="blue"
            style={{ fontSize: 14 }}
            onClick={handleCloseTicket}
          >
            Mark Resolved
          </Button>
        )}
      </Grid>
      <Grid xs={12} container>
        <Grid xs={4}>
          <div className={styles.dataContainer}>
            <div className={styles.boxTitle}>Ticket</div>
            <div className={styles.row}>
              <div className={`${styles.data} ${styles.flex2}`}>
                <div className={styles.heading}>Subject</div>
                <div className={styles.subject}>
                  {ticket.data.info.subject
                    ? ticket.data.info.subject
                    : `Ticket ${ticket._id}`}
                </div>
              </div>
              <div className={`${styles.data} ${styles.flex1}`}>
                <div className={styles.heading}>Status</div>
                <div
                  style={statusStyle(
                    ticketStatusToColor(ticket.data.info.status)
                  )}
                >
                  {capitalize(solvedToResolved(ticket.data.info.status))}
                </div>
              </div>
            </div>
            <div className={styles.row}>
              <div className={`${styles.data} ${styles.flex2}`}>
                <div className={styles.heading}>Email</div>

                <div className={styles.value}>
                  {ticket.data?.replyTo?.join(" / ")}
                </div>
              </div>

              <div className={`${styles.data} ${styles.flex1}`}>
                <div className={styles.heading}>Priority</div>

                <div className={styles.value}>
                  <Circle
                    size={15}
                    color={priorityToColor(ticket.data.info.priority)}
                  />
                  {capitalize(ticket.data.info.priority)}
                </div>
              </div>
            </div>
            <div className={styles.row}>
              <div className={`${styles.data} ${styles.flex2}`}>
                <div className={styles.heading}>Created</div>

                <div className={styles.value}>
                  {formatDate(ticket.createdAt)}
                </div>
              </div>
              <div className={`${styles.data} ${styles.flex1}`}>
                <div className={styles.heading}>Assignees</div>

                <div className={styles.value}>
                  {ticket.data.assignees && ticket.data.assignees.length
                    ? ticket.data.assignees
                        ?.map(
                          (assignee) =>
                            `${assignee.data.info.firstName ?? ""} ${
                              assignee?.data.info.lastName ?? ""
                            }`
                        )
                        .join(", ")
                    : "No assignees"}
                </div>
              </div>
            </div>

            <div className={styles.data}>
              <div className={styles.heading}>Description</div>

              <div className={styles.value}>{ticket.data.info.description}</div>
            </div>
          </div>
        </Grid>
        {ticket.data.deal && (
          <Grid xs={5} container>
            <Grid xs={6}>
              <div className={styles.dataContainer}>
                <div className={styles.boxTitle}>Loan</div>
                <div className={styles.row}>
                  <div className={`${styles.data} ${styles.flex2}`}>
                    <div className={styles.heading}>Customer</div>
                    <div className={styles.value}>
                      {formatCustomersNames(ticket.data.deal)}
                    </div>
                  </div>
                </div>
                <div className={styles.row}>
                  <div className={`${styles.data} ${styles.flex2}`}>
                    <div className={styles.heading}>VIN</div>
                    <div className={styles.value}>
                      {ticket.data.deal?.data.info?.vehicle?.VIN ?? ""}
                    </div>
                  </div>
                </div>
                <div className={styles.row}>
                  <div className={`${styles.data} ${styles.flex2}`}>
                    <div className={styles.heading}>Type</div>
                    <div className={styles.value}>
                      {ticket.data.deal?.data.info?.type ?? ""}
                    </div>
                  </div>
                </div>
                <div className={styles.row}>
                  <div className={`${styles.data} ${styles.flex2}`}>
                    <div className={styles.heading}>Contract date</div>
                    <div className={styles.value}>
                      {formatDate(
                        ticket.data.deal?.data.info.dealDates?.contractDate
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </Grid>
            <Grid xs={6}>
              <div className={styles.dataContainer}>
                <div className={styles.boxTitle}>DMV information</div>
                <div className={styles.row}>
                  <div className={`${styles.data} ${styles.flex2}`}>
                    <div className={styles.heading}>DMV</div>
                    {ticket.data?.titleIssue?.data.info?.dmvInfo?.message && (
                      <Button
                        className={styles.button}
                        variant="blue"
                        onClick={handleOpenDialog}
                      >
                        Open DMV check result
                      </Button>
                    )}
                  </div>
                </div>
                <div className={styles.row}>
                  <div className={`${styles.data} ${styles.flex2}`}>
                    <div className={styles.heading}>Documents</div>

                    {renderDocuments(
                      ticket,
                      "Title Registartion",
                      "titleRegistrationInfo"
                    )}
                    {renderDocuments(
                      ticket,
                      "Old Title Registartion",
                      "oldTitleRegistrationInfo"
                    )}

                    {renderDocuments(ticket, "Receipt", "receiptInfo")}
                  </div>
                </div>
              </div>
            </Grid>
          </Grid>
        )}
        <Grid xs={3}>
          <LenderNotes ticket={ticket} />
        </Grid>
      </Grid>

      {ticket.data.titleIssue?.data.info.dmvInfo.message && (
        <DmvCheckDialog
          open={open}
          setOpen={setOpen}
          text={ticket.data.titleIssue.data.info.dmvInfo.message}
        />
      )}
    </Grid>
  );
}
